import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { DescriptionListItem, DescriptionListTemplateConfig } from '@flowx/core-sdk'

import { getBoolExpressionSignal, getComputedValueSignal } from '../helpers'

@Component({
  selector: 'flx-description-list-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule],
  templateUrl: './description-list-wrapper.component.html',
  styleUrl: './description-list-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxDescriptionListWrapperComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<DescriptionListTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  readonly isDynamicDataSource = computed(() => !!this.flowxProps()?.isDynamicDataSource)

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  itemsSource: Signal<DescriptionListItem[]>

  readonly items = computed(() => {
    if (this.isDynamicDataSource()) {
      const resolved = this.itemsSource()
      return Array.isArray(resolved) ? resolved : []
    }
    return this.flowxProps()?.items ?? []
  })
  readonly layout = computed(() => this.flowxProps()?.layout ?? 'horizontal')

  readonly layoutClass = computed(() => `description-list--${this.layout()}`)
  readonly hostClass = computed(() =>
    [this.displayOptions().className, this.layoutClass()].filter(Boolean).join(' ')
  )

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      const templateConfig = this.templateConfig()
      this.isHidden = getBoolExpressionSignal(props.hide, templateConfig)
      // `getComputedValueSignal` evaluates the expression via `new Function` and preserves the
      // raw resolved value (array), unlike `getDynamicPropSignal` which stringifies via text
      // interpolation — required to keep `items` as actual DescriptionListItem objects.
      this.itemsSource = getComputedValueSignal(
        props.itemsSource || '[]',
        templateConfig
      ) as unknown as Signal<DescriptionListItem[]>
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
